import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import DatePicker from 'components/shared/form/DatePicker';
import { END_DATE, START_DATE } from 'constants/filterKeysConstants';
import useIsMobile from 'hooks/useIsMobile';

import DatePickerShortcuts from './DatePickerShortcuts';

type Props = {
  gtmPrefix: string;
  filters: Record<string, any>;
  setSelectedFilters: (filters: Record<string, any>) => void;
  fullWidthOnMobile?: boolean;
};

export default function InsightsDateRangePicker({
  gtmPrefix,
  filters,
  setSelectedFilters,
  fullWidthOnMobile = true,
}: Props) {
  const isMobile = useIsMobile();

  const startDateFilterValue = filters[START_DATE] as Date;
  const endDateFilterValue = filters[END_DATE] as Date;
  const [selectedStartDate, setSelectedStartDate] = useState<Date>(startDateFilterValue);
  const [selectedEndDate, setSelectedEndDate] = useState<Date>(endDateFilterValue);

  // Sync local state when the store resets (e.g., client switch)
  useEffect(() => setSelectedStartDate(startDateFilterValue), [startDateFilterValue]);
  useEffect(() => setSelectedEndDate(endDateFilterValue), [endDateFilterValue]);

  const maxDate = useMemo(() => new Date(), []);
  const minDate = useMemo(() => new Date('05/01/2020'), []);

  const DatePickerFooter = ({ dateInput }: { dateInput: any }) => (
    <DatePickerShortcuts
      startDate={selectedStartDate}
      endDate={selectedEndDate}
      datePickerRef={dateInput}
      setDates={([startDate, endDate]: Date[]) => {
        setSelectedStartDate(startDate);
        setSelectedEndDate(endDate);
        setSelectedFilters({ [START_DATE]: startDate, [END_DATE]: endDate });
      }}
    />
  );

  return (
    <Container>
      <DatePicker
        data-gtm-id={`${gtmPrefix}StartDateFilter`}
        $fullWidth={fullWidthOnMobile && isMobile}
        onCalendarClose={() => setSelectedFilters({ [START_DATE]: selectedStartDate })}
        onChange={(val) => setSelectedStartDate(val as Date)}
        selected={selectedStartDate}
        maxDate={selectedEndDate}
        minDate={minDate}
        clearable={false}
        renderFooter={DatePickerFooter}
        popperPlacement='bottom-start'
      />
      <Emdash>&mdash;</Emdash>
      <DatePicker
        data-gtm-id={`${gtmPrefix}EndDateFilter`}
        $fullWidth={fullWidthOnMobile && isMobile}
        onCalendarClose={() => setSelectedFilters({ [END_DATE]: selectedEndDate })}
        onChange={(val) => setSelectedEndDate(val as Date)}
        selected={selectedEndDate}
        maxDate={maxDate}
        minDate={selectedStartDate}
        clearable={false}
        renderFooter={DatePickerFooter}
        popperPlacement='bottom-start'
      />
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  align-items: center;

  input {
    max-width: 160px;

    @media ${({ theme }) => theme.devices.desktop} {
      max-width: 140px;
    }
  }

  @media (max-width: 800px) {
    width: 100%;

    > div:first-child,
    > div:last-child {
      min-width: 0;
    }

    input {
      max-width: none;
    }
  }
`;

const Emdash = styled.div`
  margin: 0 8px;
`;
